// src/crm/conversations/ManualExchangeForm.tsx
import { useState } from 'react'
import { CHANNEL_LABELS } from './channelMeta'
import type { AddManualMessageInput } from './useAddManualMessage'
import type { Creator, Deal, MessageChannel, MessageDirection } from '../../lib/types'
import { TemplatePicker } from '../templates/TemplatePicker'
import { hasUnresolvedTokens } from '../templates/resolve'

type Props = {
  creatorId: string
  contact: Creator
  deal: Deal | null
  appName: string
  onAdd: (input: AddManualMessageInput) => void
  isAdding: boolean
  onCancel: () => void
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  padding: '0.375rem 0.5rem',
  fontFamily: 'inherit',
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.8125rem',
  color: 'var(--muted)',
  fontWeight: 500,
}

const CHANNELS_MANUAL: MessageChannel[] = [
  'whatsapp', 'instagram_dm', 'tiktok_dm', 'call', 'note', 'other',
]

export function ManualExchangeForm({ creatorId, contact, deal, appName, onAdd, isAdding, onCancel }: Props) {
  const [channel, setChannel] = useState<MessageChannel>('note')
  const [direction, setDirection] = useState<MessageDirection>('out')
  const [body, setBody] = useState('')

  const unresolved = hasUnresolvedTokens(body)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    onAdd({
      creatorId,
      channel,
      direction,
      body: body.trim(),
    })
  }

  return (
    <form
      role="dialog"
      aria-label="Ajouter un échange manuel"
      onSubmit={handleSubmit}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '8px',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div
        style={{
          fontSize: '0.75rem',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        }}
      >
        Ajouter un échange manuel
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <label style={labelStyle}>
          <span>Canal</span>
          <select
            aria-label="Canal"
            value={channel}
            onChange={(e) => setChannel(e.target.value as MessageChannel)}
            style={inputStyle}
          >
            {CHANNELS_MANUAL.map((ch) => (
              <option key={ch} value={ch}>
                {CHANNEL_LABELS[ch]}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          <span>Sens</span>
          <select
            aria-label="Sens"
            value={direction}
            onChange={(e) => setDirection(e.target.value as MessageDirection)}
            style={inputStyle}
          >
            <option value="out">Sortant</option>
            <option value="in">Entrant</option>
          </select>
        </label>
      </div>

      <TemplatePicker
        channel={channel}
        contact={contact}
        deal={deal}
        appName={appName}
        onApply={({ body: b }) => setBody(b)}
      />

      <label style={labelStyle}>
        <span>Contenu</span>
        <textarea
          aria-label="Contenu"
          required
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
          placeholder="Décrivez l'échange…"
        />
      </label>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isAdding}
          style={{
            padding: '0.4375rem 1rem',
            background: 'transparent',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: 'var(--muted)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          disabled={!body.trim() || unresolved}
          onClick={() => { void navigator.clipboard.writeText(body) }}
          style={{
            padding: '0.4375rem 1rem',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: unresolved ? 'var(--dim)' : 'var(--muted)',
            fontSize: '0.875rem',
            cursor: unresolved ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Copier
        </button>
        <button
          type="submit"
          disabled={isAdding}
          style={{
            padding: '0.4375rem 1rem',
            background: 'var(--arancio)',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: isAdding ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            opacity: isAdding ? 0.6 : 1,
          }}
        >
          {isAdding ? 'Ajout…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}
